import { Link } from 'wouter';
import { footerLinks, HUB_GUIDES, HUB_TEMPLATES, type FooterLink } from '@/marketing/footer';
import { useTranslation } from 'react-i18next';
import { ScopieLogo } from '@/components/brand/ScopieLogo';
import { MARKETING_LANGUAGES, findLanguage } from '@/lib/i18n/languages';
import { LEGAL_ENTITY_NAME, LEGAL_PLACE } from '@/config/legal-entity';
import { Facebook, Linkedin, Youtube, type LucideIcon } from 'lucide-react';
import profiles from '@/marketing/profiles.data.json';

// Same storage key and call as the language selector (LanguagePillMenu), so the choice persists the same way.
const LANGUAGE_STORAGE_KEY = 'cleancal_language';

// the profiles in footer order; one is listed once its public page is in profiles.data.json (G2 is not yet: only its seller page exists)
// [name, key in profiles.data.json, icon]: the social pages show their icon beside the name, the review sites are text until their official badges are added
const PROFILE_LINKS: Array<[string, string, LucideIcon?]> = [['G2', 'g2'], ['Capterra', 'capterra'], ['GetApp', 'getapp'], ['YouTube', 'youtube', Youtube], ['Facebook', 'facebook', Facebook], ['LinkedIn', 'linkedin', Linkedin]];

const LANGUAGE_NAMES: Record<string, string> = { en: 'English', es: 'Español', 'pt-BR': 'Português' };

export function HomeFooter({ home = true }: { home?: boolean } = {}) {
  const { t, i18n } = useTranslation('home');
  const current = findLanguage(MARKETING_LANGUAGES, i18n.language).code;

  const choose = (code: string) => {
    void i18n.changeLanguage(code);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    } catch {
      // Storage blocked: the language still changes for this visit.
    }
  };

  const links = footerLinks();
  const list = (items: FooterLink[]) => (
    <ul>{items.map((l) => <li key={l.href}><Link href={l.href}>{l.label}</Link></li>)}</ul>
  );

  return (
    <footer className="hf">
      <div className="wrap">
        <div className="hf-top">
          <a className="logo" href={home ? '#top' : '/'} aria-label={t('landing.nav.home')}>
            <ScopieLogo variant="dark" />
          </a>
          <div className="langs" role="group" aria-label={t('landing.footer.language')}>
            {MARKETING_LANGUAGES.map(({ code }) => (
              <button
                key={code}
                type="button"
                lang={code}
                aria-pressed={current === code}
                onClick={() => choose(code)}
              >
                {LANGUAGE_NAMES[code]}
              </button>
            ))}
          </div>
        </div>
        {/* only published pages are linked (src/marketing/footer.ts); the page names of the trades, templates and guides are English like the pages themselves */}
        <div className="hf-cols">
          {links.industryGroups.length > 0 && (
            <nav className="hf-col hf-industries" aria-label={t('landing.footer.industries')}>
              <p className="hf-h">{t('landing.footer.industries')}</p>
              <div className="hf-groups">
                {links.industryGroups.map((g) => (
                  <div key={g.group ?? 'all'}>
                    {g.group && <p className="hf-sub">{g.group}</p>}
                    {list(g.links)}
                  </div>
                ))}
              </div>
              <ul><li><Link href="/for">{t('landing.footer.allIndustries')}</Link></li></ul>
            </nav>
          )}
          {links.templates.length > 0 && (
            <nav className="hf-col" aria-label={t('landing.footer.templates')}>
              <p className="hf-h">{t('landing.footer.templates')}</p>
              {list([...links.templates, { href: HUB_TEMPLATES, label: t('landing.footer.allTemplates') }])}
            </nav>
          )}
          {links.guides.length > 0 && (
            <nav className="hf-col" aria-label={t('landing.footer.guides')}>
              <p className="hf-h">{t('landing.footer.guides')}</p>
              {list([...links.guides, { href: HUB_GUIDES, label: t('landing.footer.allGuides') }])}
            </nav>
          )}
          {links.comparisons.length > 0 && (
            <nav className="hf-col" aria-label={t('landing.footer.compare')}>
              <p className="hf-h">{t('landing.footer.compare')}</p>
              {list(links.comparisons)}
            </nav>
          )}
          <nav className="hf-col" aria-label={t('landing.footer.company')}>
            <p className="hf-h">{t('landing.footer.company')}</p>
            {list([
              { href: '/about', label: t('landing.footer.about') },
              { href: '/contact', label: t('landing.footer.contact') },
              { href: '/terms', label: t('landing.footer.terms') },
              { href: '/privacy', label: t('landing.footer.privacy') },
              { href: '/cookies', label: t('landing.footer.cookies') },
            ])}
          </nav>
        </div>
        <div className="hf-apps" role="group" aria-label={t('landing.footer.apps')}>
          {/* not links: the apps are not out yet */}
          <span className="hf-badge">{t('landing.footer.appStore')}<small>{t('landing.footer.comingSoon')}</small></span>
          <span className="hf-badge">{t('landing.footer.googlePlay')}<small>{t('landing.footer.comingSoon')}</small></span>
        </div>
        {/* the image is our own copy of AlternativeTo's badge (public/badges), so a visit never contacts their server */}
        <div className="hf-listed" role="group" aria-label={t('landing.footer.findUs')}>
          <p className="hf-h">{t('landing.footer.findUs')}</p>
          <div className="hf-listed-row">
            <a href="https://alternativeto.net/software/scopie/about/?utm_source=badge&utm_medium=referral" target="_blank" rel="noopener">
              <img src="/badges/alternativeto-compact-dark.svg" alt="Scopie | AlternativeTo" width={244} height={83} loading="lazy" />
            </a>
            {/* the profiles (src/marketing/profiles.data.json, the same list as the Organization sameAs) */}
            {PROFILE_LINKS.filter(([, key]) => key in profiles).map(([name, key, Icon]) => (
              <a key={key} className="hf-site" href={profiles[key as keyof typeof profiles]} target="_blank" rel="noopener">
                {Icon && <Icon aria-hidden="true" width={24} height={24} />}
                {name}
              </a>
            ))}
          </div>
        </div>
        <small>{t('landing.footer.copyright', { year: new Date().getFullYear(), entity: LEGAL_ENTITY_NAME, place: LEGAL_PLACE })}</small>
      </div>
    </footer>
  );
}
